import { useScrollReveal, useScrollRevealGroup } from '@/hooks/useScrollReveal';

const plans = [
  {
    title: 'По вашей базе',
    price: '40 000',
    contactDesc: 'от 2 000 контактов',
    desc: 'Вы предоставляете свою базу контактов, мы делаем всё остальное: прогреваем номера, запускаем рассылку, собираем ответы.',
    features: [
      'Рассылка по вашей базе контактов',
      '3 канала: Telegram, WhatsApp, Max',
      'Персонализированные сообщения',
      'Прогрев номеров перед рассылкой',
      'Защита от блокировок',
      'Отчёт по результатам',
      '2 000 контактов включено',
    ],
    highlighted: false,
    badge: null,
  },
  {
    title: 'С нашей базой',
    price: '60 000',
    contactDesc: 'от 2 000 контактов',
    desc: 'Мы собираем целевую базу под вашу нишу, плюс полный цикл: прогрев, рассылка, сбор лидов. Идеально для холодного старта.',
    features: [
      'Сбор базы с нашей стороны',
      'Таргетинг по нише и интересам',
      '3 канала: Telegram, WhatsApp, Max',
      'Персонализированные сообщения',
      'Прогрев номеров перед рассылкой',
      'Защита от блокировок',
      'Отчёт по результатам + аналитика',
      '2 000 контактов включено',
    ],
    highlighted: true,
    badge: 'Рекомендуем',
  },
];

export default function MMPricing() {
  const { ref: labelRef, revealed: labelRevealed } = useScrollReveal();
  const { ref: titleRef, revealed: titleRevealed } = useScrollReveal({ delay: 150 });
  const { ref: descRef, revealed: descRevealed } = useScrollReveal({ delay: 250 });
  const { containerRef: cardsRef, revealedCount: cardsRevealed } = useScrollRevealGroup<HTMLDivElement>(2);

  return (
    <section id="mm-pricing" className="py-20 md:py-28 bg-background-100">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="text-center mb-12 md:mb-16">
          <span ref={labelRef} className={`text-[12px] font-semibold uppercase tracking-[0.18em] text-primary-600 reveal-fade-up ${labelRevealed ? 'revealed' : ''}`}>
            Стоимость
          </span>
          <h2 ref={titleRef} className={`mt-4 font-display font-extrabold text-[34px] md:text-[46px] leading-[1.1] tracking-tightest text-foreground-950 reveal-fade-up ${titleRevealed ? 'revealed' : ''}`}>
            Выберите
            <br />
            свой вариант
          </h2>
          <p ref={descRef} className={`mt-3 text-[14.5px] text-foreground-500 max-w-xl mx-auto reveal-fade-up ${descRevealed ? 'revealed' : ''}`}>
            Цена за 2 000 контактов. Большие объёмы обсуждаются индивидуально — чем больше, тем выгоднее.
          </p>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {plans.map((plan, i) => (
            <div
              key={plan.title}
              className={`relative rounded-xl p-6 md:p-7 flex flex-col reveal-fade-up ${i < cardsRevealed ? 'revealed' : ''} ${
                plan.highlighted
                  ? 'bg-primary-500 text-background-50 ring-2 ring-primary-500 ring-offset-2 ring-offset-background-100'
                  : 'bg-background-50 border border-background-200'
              }`}
              style={i < cardsRevealed ? { transitionDelay: `${i * 150}ms` } : undefined}
            >
              {plan.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[11px] font-semibold bg-background-50 text-primary-600 rounded-full whitespace-nowrap">
                  {plan.badge}
                </span>
              )}

              <h3 className={`font-heading text-lg font-bold ${plan.highlighted ? 'text-background-50' : 'text-foreground-950'}`}>
                {plan.title}
              </h3>
              <p className={`text-sm mt-1 ${plan.highlighted ? 'text-background-50/80' : 'text-foreground-600'}`}>
                {plan.contactDesc}
              </p>

              <div className="mt-5 mb-1">
                <span className={`text-3xl md:text-4xl font-heading font-extrabold ${plan.highlighted ? 'text-background-50' : 'text-foreground-950'}`}>
                  {plan.price} ₽
                </span>
              </div>

              <p className={`text-[13px] mt-4 leading-relaxed ${plan.highlighted ? 'text-background-50/80' : 'text-foreground-500'}`}>
                {plan.desc}
              </p>

              <ul className="mt-6 flex flex-col gap-2.5 flex-grow">
                {plan.features.map((feat) => (
                  <li key={feat} className="flex items-start gap-2.5">
                    <span className={`flex-shrink-0 mt-0.5 ${plan.highlighted ? 'text-background-50' : 'text-primary-500'}`}>
                      <i className="ri-check-line text-sm"></i>
                    </span>
                    <span className={`text-sm ${plan.highlighted ? 'text-background-50/90' : 'text-foreground-700'}`}>
                      {feat}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href="#mm-cta"
                className={`mt-7 w-full py-3 text-sm font-semibold rounded-md text-center transition-colors whitespace-nowrap cursor-pointer ${
                  plan.highlighted
                    ? 'bg-background-50 text-primary-600 hover:bg-background-100'
                    : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                }`}
              >
                Оставить заявку
              </a>
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-[13px] text-foreground-500">
          Крупные объёмы (10 000+ контактов) —{' '}
          <a href="https://t.me/anna_aigam" target="_blank" rel="noopener noreferrer" className="text-primary-600 font-medium hover:text-primary-700 transition-colors cursor-pointer">
            обсудим индивидуально
          </a>
        </p>
      </div>
    </section>
  );
}